import {Signal} from '../signals-screen/types';
import {SelectBrandItem} from '../../services/dashboard.service';
import {DeviceFilterType} from '../../services/types';
import {DateRangeValue} from '../../utils/time';

export type HistoryMessage = {
  role: string;
  content: string;
}

export type FocusArea = {
  title: string;
  subQuestions: string[];
}

export type FocusAreasMap = Record<string, { subQuestions: string[] }>;

export type WizardPayload = {
  type: string;
  focusAreas: Record<string, FocusArea>;
  brands: string[];
  competitors: string[];
  geos: string[];
  startDate: string;
  endDate: string;
};

export type AlertPayload = {
  alert_type: string;
  metric: string;
  change: {
    direction: string;
    percent: string;
  };
  date_before: string;
  date_after: string;
  brand_key: string;
}

export type WidgetContext = {
  name?: DashboardWidget;
  metrics: string[];
  myBrand: string;
  myCompetitors: string[];
  dateRange: DateRangeValue;
  countries: string[];
  device: DeviceFilterType;
}

export type CopilotMessagePayload = {
  requestId: string;
  userPrompt: string;
  sessionContext: {
    sessionId: string;
    customerName: string;
    customerBrandsList: string[];
  };
  workflowContext?: string;
  conversationHistory: HistoryMessage[];
  agencyId: string;
  debug?: boolean;
  wizard?: WizardPayload;
  alert?: AlertPayload;
  signal?: Signal;
  widgetContext?: WidgetContext;
}

export type CopilotMessageResponse = {
  requestId: string;
  sessionId: string;
  userId: string;
  status: string;
  timestamp: string;
  answer: {
    text: string;
    insights: string;
  };
  graph: graphSpec[];
  followUpQuestions: string[];
  creativesURLs: string[];
  originalResponse?:any;
  lambdaId?: string;
}

export type Wizard = {
    type:  string;
    focusAreas: FocusAreasMap;
    title: string;
    description: string;
    tileSvg: string;
    wizardSvg: string;
}

export type graphSpec = {
  title: string;
  type: 'bar'| 'line' | 'doughnut';
  url?: string;
  x: string[];
  series: graphSeries[];
  y_is_percentage?: boolean;
  x_axis: string;
  y_axis: string;
}

export type graphSeries = {
  name: string;
  y: number[];
  unit?: string;
}

export enum DashboardWidget {
  SPEND_ANALYSIS = 'Spend Analysis',
  CREATIVE_GALLERY = 'Creative Gallery',
  CREATIVE_GALLERY_COMPETITION = 'Creative Gallery Competition',
  CREATIVE_GALLERY_MESSAGING = 'Creative Gallery Messaging',
  PERFORMANCE_MAP = 'Performance Map',
  PERFORMANCE_OVER_TIME = 'Performance Over Time',
  MY_COMPETITION_OVERVIEW = 'Overview',
}

const CREATIVE_GALLERY_COMPETITORS_QUESTIONS = [
  "Which phrases are most frequently used in my competitors' ads?",
  "What factors are contributing to my competitor's top creative's success?",
  "What can I do to improve my creative effectiveness based on the competition?"
];

export const WIDGET_QUESTIONS_MAP = {
  [DashboardWidget.SPEND_ANALYSIS]: [
    "How does my spend efficiency compare against industry benchmarks?",
    "How can I scale my campaign CTR success, based on my competitions strategies?",
    "Where am I seeing the biggest gaps in efficiency vs. my competitors?",
    "What are the biggest drivers of inefficient spend in my current dataset?"
  ],
  [DashboardWidget.CREATIVE_GALLERY]: [
    "What are my most effective creatives based on CTR, TruAttention™, and impressions?",
    "What can I do to improve my creative effectiveness based on the competition?",
    "What factors are contributing to the success of my top creatives?"
  ],
  [DashboardWidget.CREATIVE_GALLERY_COMPETITION]: CREATIVE_GALLERY_COMPETITORS_QUESTIONS,
  [DashboardWidget.CREATIVE_GALLERY_MESSAGING]: CREATIVE_GALLERY_COMPETITORS_QUESTIONS,
  [DashboardWidget.PERFORMANCE_MAP]: [
    "Which states should my brand prioritize for expansion based on cost efficiency and performance (TruAttention™, dwell time, CTR)?",
    "Which states are my competitors seeing the most success in (highest CTR, highest TruAttention™, lowest CPM)?",
    "Based on competitor success, which states should I target?",
    "Which of the countries I’m targeting are underperforming or overperforming compared to my competitors, and why?"
  ],
  [DashboardWidget.PERFORMANCE_OVER_TIME]: [
    "Are gaps versus competitors widening or narrowing over time?",
    "What explains the performance trends across the selected brands?",
  ],
  [DashboardWidget.MY_COMPETITION_OVERVIEW]: [
    "My brand’s CPM is higher than my competitors’, but my TruAttention™ is lower. Why? How can I improve this?",
    "My brand is achieving a higher CTR than my competitors with a lower CPM. Why? How can I scale this approach?",
    "Which audience segments generate the highest TruAttention™ for my brand compared to my competitors?"
  ]
}

export const ASK_POLARIS_SIDE_DRAWER_WIDTH = '700px';
